import React, { useState, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AccountModal = ({ isOpen, onClose, onSave, account, allAccounts }) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    type: '',
    parentId: '',
    description: '',
    balance: 0,
    isActive: true
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (account) {
      setFormData({
        code: account?.code || '',
        name: account?.name || '',
        type: account?.type || '',
        parentId: account?.parentId || '',
        description: account?.description || '',
        balance: account?.balance || 0,
        isActive: account?.isActive !== undefined ? account?.isActive : true
      });
    } else {
      setFormData({
        code: '',
        name: '',
        type: '',
        parentId: '',
        description: '',
        balance: 0,
        isActive: true
      });
    }
    setErrors({});
  }, [account, isOpen]);

  const accountTypes = [
    { value: 'Asset', label: 'Asset' },
    { value: 'Liability', label: 'Liability' },
    { value: 'Equity', label: 'Equity' },
    { value: 'Revenue', label: 'Revenue' },
    { value: 'Expense', label: 'Expense' }
  ];

  const getParentAccountOptions = () => {
    const flattenAccounts = (accounts, level = 0) => {
      let result = [];
      accounts?.forEach(acc => {
        if (account && acc?.id === account?.id) return;
        result?.push({
          value: acc?.id,
          label: `${'  '?.repeat(level)}${acc?.code} - ${acc?.name}`,
          disabled: acc?.children && acc?.children?.length > 0 && level > 2
        });
        if (acc?.children) {
          result = result?.concat(flattenAccounts(acc?.children, level + 1));
        }
      });
      return result;
    };

    return [
      { value: '', label: 'None (Top Level)' },
      ...flattenAccounts(allAccounts)
    ];
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.code?.trim()) {
      newErrors.code = 'Account code is required';
    } else if (formData?.code?.length < 3 || formData?.code?.length > 10) {
      newErrors.code = 'Account code must be 3-10 characters';
    } else if (!/^[A-Za-z0-9]+$/?.test(formData?.code)) {
      newErrors.code = 'Account code can only contain letters and numbers';
    } else {
      const isDuplicate = allAccounts?.some(acc => 
        acc?.code === formData?.code && (!account || acc?.id !== account?.id)
      );
      if (isDuplicate) {
        newErrors.code = 'Account code already exists';
      }
    }

    if (!formData?.name?.trim()) {
      newErrors.name = 'Account name is required';
    }

    if (!formData?.type) {
      newErrors.type = 'Account type is required';
    }

    if (formData?.balance && isNaN(parseFloat(formData?.balance))) {
      newErrors.balance = 'Opening balance must be a valid number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave({
        ...account,
        ...formData,
        balance: parseFloat(formData?.balance) || 0,
        id: account?.id || `acc_${Date.now()}`
      });
      onClose();
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1030 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">
            {account ? 'Edit Account' : 'Add New Account'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          />
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 md:space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Input
              label="Account Code"
              type="text"
              placeholder="e.g., 1000"
              value={formData?.code}
              onChange={(e) => handleChange('code', e?.target?.value)}
              error={errors?.code}
              required
              description="3-10 characters, letters and numbers only"
            />

            <Select
              label="Account Type"
              options={accountTypes}
              value={formData?.type}
              onChange={(value) => handleChange('type', value)}
              error={errors?.type}
              required
              placeholder="Select account type"
            />
          </div>

          <Input
            label="Account Name"
            type="text"
            placeholder="e.g., Cash in Bank"
            value={formData?.name}
            onChange={(e) => handleChange('name', e?.target?.value)}
            error={errors?.name}
            required
          />

          <Select
            label="Parent Account"
            options={getParentAccountOptions()}
            value={formData?.parentId}
            onChange={(value) => handleChange('parentId', value)}
            placeholder="Select parent account (optional)"
            description="Leave empty for top-level account"
            searchable
          />

          <Input
            label="Description"
            type="text"
            placeholder="Brief description of the account"
            value={formData?.description}
            onChange={(e) => handleChange('description', e?.target?.value)}
          />

          <Input
            label="Opening Balance"
            type="number"
            step="0.01"
            placeholder="0.00"
            value={formData?.balance}
            onChange={(e) => handleChange('balance', e?.target?.value)}
            error={errors?.balance}
            description="Initial balance for this account"
          />

          <div className="flex items-center gap-3 p-4 bg-muted rounded-md">
            <input
              type="checkbox"
              id="isActive"
              checked={formData?.isActive}
              onChange={(e) => handleChange('isActive', e?.target?.checked)}
              className="w-4 h-4 md:w-5 md:h-5 rounded border-border text-primary focus:ring-2 focus:ring-primary/30"
            />
            <label htmlFor="isActive" className="font-body text-sm md:text-base text-foreground cursor-pointer">
              Account is active
            </label>
          </div>

          <div className="flex flex-col-reverse md:flex-row gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              fullWidth
              className="md:flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth
              className="md:flex-1"
              iconName="Save"
              iconPosition="left"
            >
              {account ? 'Update Account' : 'Create Account'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccountModal;